import * as React from 'react';

/**
 * Kamera cercevesindeki kimlik kilavuzu.
 *
 * Sorun: gorevliye "on yuz", sonra "arka yuz" deniyordu ama ekranda BELGENIN
 * KENDISI hic gorunmuyordu. Adimlar numaraliydi, uyari cumlesi hukuki bir dille
 * yaziliydi ve ilk kareden sonra hicbir sey "oldu, simdi cevir" demiyordu.
 * Insanlar cogu zaman on yuzden sonra duruyordu.
 *
 * Cozum: cerceveye dusuk opaklikta bir HAYALET KART cizilir - belgeyi nereye,
 * hangi yonde tutacagi bakar bakmaz anlasilir. On yuz alininca kart 3B olarak
 * CEVRILIR ve arka yuz sablonuna donusur: ekrandaki hareket, elin yapacagi
 * hareketin aynisidir. Arka yuz sablonunda "anne adi / baba adi" satirlari
 * adiyla yazar; boylece ikinci karenin NEDEN istendigi de anlatilmis olur.
 *
 * Tasarim kaynagi: Lazyweb raporu "Improve ID scanning flow" - Hipotez #1
 * "Kart Cevir Anim." (iris-id ve scanbot'un hayalet kart kilavuzu; Airbnb'nin
 * kimlik cekim cercevesi).
 *
 * Renk `currentColor`dur: acik zeminli resepsiyon ekraninda da koyu lacivert
 * telefon ekraninda da ayni bilesen kullanilir.
 */

/** Kimlik karti oranı (ID-1: 85.6 x 54 mm). */
const EN = 214;
const BOY = 135;

function OnYuz() {
  return (
    <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <rect x="1.5" y="1.5" width={EN - 3} height={BOY - 3} rx="10" />
      {/* fotograf */}
      <rect x="16" y="30" width="52" height="66" rx="5" />
      {/* cip */}
      <rect x="16" y="104" width="24" height="16" rx="3" />
      {/* baslik + alan satirlari */}
      <path d="M84 34h74" strokeWidth="4" />
      <path d="M84 56h96M84 74h62M84 92h84" strokeWidth="3" opacity=".75" />
      {/* MRZ */}
      <path d="M16 128h182" strokeWidth="3" strokeDasharray="7 5" opacity=".55" />
    </g>
  );
}

function ArkaYuz() {
  return (
    <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <rect x="1.5" y="1.5" width={EN - 3} height={BOY - 3} rx="10" />
      {/*
        Arka yuzde ARANAN alanlar adiyla yazar. Gorevli ikinci karenin
        neden istendigini aciklama okumadan gorur.
      */}
      <text
        x="18"
        y="34"
        fill="currentColor"
        stroke="none"
        fontSize="12"
        fontWeight="700"
        letterSpacing="1.5"
      >
        ANNE ADI
      </text>
      <path d="M18 44h116" strokeWidth="3" opacity=".6" />
      <text x="18" y="70" fill="currentColor" stroke="none" fontSize="12" fontWeight="700" letterSpacing="1.5">
        BABA ADI
      </text>
      <path d="M18 80h104" strokeWidth="3" opacity=".6" />
      <text x="18" y="106" fill="currentColor" stroke="none" fontSize="12" fontWeight="700" letterSpacing="1.5">
        DOĞUM YERİ
      </text>
      <path d="M18 116h82" strokeWidth="3" opacity=".6" />
      {/* MRZ blogu */}
      <path d="M150 96h48M150 106h48M150 116h48" strokeWidth="3" strokeDasharray="5 4" opacity=".55" />
    </g>
  );
}

export function KimlikRehberi({
  yuz,
  akista = false,
}: {
  yuz: 'on' | 'arka';
  /**
   * Akis icinde mi cizilsin?
   *
   * Kamera ACIKKEN kilavuz canli goruntunun UZERINE bindirilir (mutlak).
   * Kamera KAPALIYKEN ise ayni yere aciklama metni de geliyor ve ikisi
   * ust uste binip ikisini de okunmaz yapiyordu; o durumda kart normal
   * akista, metnin ustunde durur.
   */
  akista?: boolean;
}) {
  return (
    <div
      aria-hidden="true"
      className={
        akista
          ? 'flex w-full items-center justify-center'
          : 'pointer-events-none absolute inset-0 flex items-center justify-center'
      }
      style={{ perspective: '1000px' }}
    >
      <div
        data-rehber-kart={yuz}
        className={
          'relative transition-transform duration-[900ms] ease-[cubic-bezier(.2,.8,.2,1)] [transform-style:preserve-3d] ' +
          (akista ? 'w-[190px] max-w-[70%]' : 'w-[62%] max-w-[280px]')
        }
        style={{
          aspectRatio: `${EN} / ${BOY}`,
          /*
           * Cevirme, basari perdesi SONERKEN calissin (perde 3 sn, %74'te
           * sonmeye baslar ~ 2,2 sn). Boylece perde kalktiginda gorevli kartin
           * arka yuzunu doner halde gorur - hareketi kacirmaz.
           */
          transitionDelay: '2100ms',
          transform: yuz === 'arka' ? 'rotateY(180deg)' : undefined,
        }}
      >
        <svg
          viewBox={`0 0 ${EN} ${BOY}`}
          className="absolute inset-0 size-full opacity-30 [backface-visibility:hidden]"
        >
          <OnYuz />
        </svg>
        <svg
          viewBox={`0 0 ${EN} ${BOY}`}
          className="absolute inset-0 size-full opacity-30 [backface-visibility:hidden] [transform:rotateY(180deg)]"
        >
          <ArkaYuz />
        </svg>
      </div>
    </div>
  );
}

/**
 * "Başarılı — şimdi arka yüzü taratın" sahnesi.
 *
 * Ilk kareden sonra hicbir sey olmuyordu; gorevli isin yurudugunu anlamiyor,
 * cogu zaman orada duruyordu. Kucuk bir rozet de yetmedi: cerceve ortasinda
 * duran metinle ust uste biniyordu ve iki mesaj birbirini yiyordu.
 *
 * Artik TEK bir sahne var. Cerceve bulaniklasir, sirayla tik, "Başarılı!" ve
 * "Şimdi arka yüzü taratın" gelir; kucuk bir kart sessizce cevrilerek ne
 * yapilacagini gosterir. Perde sonerken arkadaki kilavuz kart da cevrilir,
 * yani perde kalktiginda ekran zaten yeni adimda olur.
 *
 * Ekrani KILITLEMEZ (`pointer-events-none`): gorevli beklemeden devam edebilir.
 */
export function BasariRozeti({
  baslik = 'Başarılı!',
  altMetin = 'Şimdi arka yüzü taratın',
}: {
  baslik?: string;
  altMetin?: string;
}) {
  return (
    <div
      role="status"
      className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 rounded-2xl bg-slate-950/65 backdrop-blur-md [animation:perde_3s_ease-out_both]"
    >
      {/* Tik: iki halka disari acilir, daire yaylanarak gelir, tik cizilir */}
      <span className="relative flex size-20 items-center justify-center">
        <span className="absolute inset-0 rounded-full border-2 border-emerald-300 [animation:ring_1.2s_.15s_ease-out_both]" />
        <span className="absolute inset-0 rounded-full border-2 border-emerald-300 [animation:ring_1.2s_.5s_ease-out_both]" />
        <span className="flex size-20 items-center justify-center rounded-full bg-emerald-500 text-white shadow-xl [animation:basariPop_.6s_cubic-bezier(.2,1.4,.4,1)_both]">
          <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
            <path
              d="M4 12.5 9.5 18 20 6.5"
              strokeDasharray="26"
              strokeDashoffset="26"
              style={{ animation: 'draw .5s .32s ease-out both' }}
            />
          </svg>
        </span>
      </span>

      <div className="flex flex-col items-center gap-1 px-6 text-center">
        <p className="text-[22px] font-bold text-white [animation:basariYaz_.5s_.38s_cubic-bezier(.2,.8,.2,1)_both] sm:text-2xl">
          {baslik}
        </p>
        <p className="max-w-xs text-[15px] text-white/85 [animation:basariYaz_.5s_.55s_cubic-bezier(.2,.8,.2,1)_both]">
          {altMetin}
        </p>
      </div>

      {/* Cevirme jesti: kucuk kart doner, "ters cevir" demeye gerek kalmaz */}
      <span
        className="[animation:basariYaz_.5s_.72s_cubic-bezier(.2,.8,.2,1)_both]"
        style={{ perspective: '600px' }}
      >
        <span className="block [animation:kartCevir_2.2s_.9s_ease-in-out_infinite] [transform-style:preserve-3d]">
          <svg width="76" height="48" viewBox="0 0 214 135" fill="none" stroke="currentColor" strokeWidth="6" className="text-white/65">
            <rect x="3" y="3" width="208" height="129" rx="14" />
            <path d="M30 40h44M30 66h60M30 92h38" strokeLinecap="round" opacity=".8" />
          </svg>
        </span>
      </span>
    </div>
  );
}
